import {AfterViewInit, Component, ElementRef, HostListener, Input, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-avatar-crop-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './avatar-crop-modal.component.html',
  styleUrl: './avatar-crop-modal.component.scss',
})
export class AvatarCropModalComponent implements AfterViewInit {
  @Input({required: true}) imageDataUrl = '';
  @Input() outputSize = 512;
  @ViewChild('cropCanvas') cropCanvasRef!: ElementRef<HTMLCanvasElement>;

  protected zoom = 1;
  protected minZoom = 0.1;
  protected maxZoom = 4;

  private readonly CANVAS_SIZE = 400;
  private readonly CROP_RADIUS = 175;
  private readonly img = new Image();
  private imgW = 0;
  private imgH = 0;
  private offsetX = 0;
  private offsetY = 0;
  private isDragging = false;
  private lastPointerX = 0;
  private lastPointerY = 0;

  constructor(public readonly activeModal: NgbActiveModal) {}

  ngAfterViewInit(): void {
    const canvas = this.cropCanvasRef.nativeElement;
    canvas.width = this.CANVAS_SIZE;
    canvas.height = this.CANVAS_SIZE;
    this.img.onload = () => {
      this.imgW = this.img.naturalWidth;
      this.imgH = this.img.naturalHeight;
      this.resetCrop();
    };
    this.img.src = this.imageDataUrl;
  }

  @HostListener('window:pointerup')
  onPointerUp(): void {
    this.isDragging = false;
  }

  @HostListener('window:pointermove', ['$event'])
  onPointerMove(e: PointerEvent): void {
    if (!this.isDragging) { return; }
    e.preventDefault();
    const rect = this.cropCanvasRef.nativeElement.getBoundingClientRect();
    const scale = this.CANVAS_SIZE / rect.width;
    this.offsetX += (e.clientX - this.lastPointerX) * scale;
    this.offsetY += (e.clientY - this.lastPointerY) * scale;
    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;
    this.clampOffsets();
    this.draw();
  }

  protected onPointerDown(e: PointerEvent): void {
    e.preventDefault();
    this.isDragging = true;
    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;
    try { (e.target as Element).setPointerCapture(e.pointerId); } catch { /* ignore */ }
  }

  protected onZoomInput(e: Event): void {
    const newZoom = parseFloat((e.target as HTMLInputElement).value);
    const CX = this.CANVAS_SIZE / 2;
    const CY = this.CANVAS_SIZE / 2;
    const ratio = newZoom / this.zoom;
    this.offsetX = CX - ratio * (CX - this.offsetX);
    this.offsetY = CY - ratio * (CY - this.offsetY);
    this.zoom = newZoom;
    this.clampOffsets();
    this.draw();
  }

  protected resetCrop(): void {
    const diameter = this.CROP_RADIUS * 2;
    this.minZoom = Math.max(diameter / this.imgW, diameter / this.imgH);
    this.maxZoom = this.minZoom * 4;
    this.zoom = this.minZoom;
    this.offsetX = (this.CANVAS_SIZE - this.imgW * this.zoom) / 2;
    this.offsetY = (this.CANVAS_SIZE - this.imgH * this.zoom) / 2;
    this.draw();
  }

  protected confirm(): void {
    const out = document.createElement('canvas');
    out.width = this.outputSize;
    out.height = this.outputSize;
    const ctx = out.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const CX = this.CANVAS_SIZE / 2;
    const CY = this.CANVAS_SIZE / 2;
    const R = this.CROP_RADIUS;
    const srcX = (CX - R - this.offsetX) / this.zoom;
    const srcY = (CY - R - this.offsetY) / this.zoom;
    const srcSize = (R * 2) / this.zoom;
    ctx.drawImage(this.img, srcX, srcY, srcSize, srcSize, 0, 0, this.outputSize, this.outputSize);
    out.toBlob((blob) => {
      if (blob) { this.activeModal.close(blob); }
    }, 'image/webp', 0.9);
  }

  private draw(): void {
    const canvas = this.cropCanvasRef?.nativeElement;
    if (!canvas || !this.imgW) { return; }
    const ctx = canvas.getContext('2d');
    if (!ctx) { return; }
    const S = this.CANVAS_SIZE;
    const CX = S / 2;
    const CY = S / 2;
    const R = this.CROP_RADIUS;

    ctx.clearRect(0, 0, S, S);
    ctx.drawImage(this.img, this.offsetX, this.offsetY, this.imgW * this.zoom, this.imgH * this.zoom);

    ctx.save();
    ctx.fillStyle = 'rgba(15,23,42,0.55)';
    ctx.beginPath();
    ctx.rect(0, 0, S, S);
    ctx.arc(CX, CY, R, 0, Math.PI * 2, true);
    ctx.fill('evenodd');
    ctx.beginPath();
    ctx.arc(CX, CY, R, 0, Math.PI * 2);
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();

  }

  private clampOffsets(): void {
    const CX = this.CANVAS_SIZE / 2;
    const CY = this.CANVAS_SIZE / 2;
    const R = this.CROP_RADIUS;
    const iw = this.imgW * this.zoom;
    const ih = this.imgH * this.zoom;
    this.offsetX = this.clamp(this.offsetX, CX + R - iw, CX - R);
    this.offsetY = this.clamp(this.offsetY, CY + R - ih, CY - R);
  }

  private clamp(val: number, min: number, max: number): number {
    return Math.min(Math.max(val, min), max);
  }
}
